/**
 * NextStepBanner — "Next: X [button]" or "Stopped: why [fix]" (BM-H3350).
 *
 * The user-ease review (30 Sep 2026) found the matter pages show where a
 * matter is but not what to do about it. The backend works the answer out
 * (hub/matter_next_step.py) and returns it as `next_step` on
 * GET /pipeline/matter/{ref}/status. This banner only draws it.
 *
 * Pass `status` when the page already holds that payload; otherwise the
 * banner fetches it. A failed fetch shows nothing rather than a guess.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Clock, OctagonAlert } from "lucide-react";
import { API } from "../config/api";
import { Button } from "./ui/button";
import { openConflictCheck } from "../utils/openConflictCheck";
import { openMatterCcl } from "../utils/openMatterCcl";

const LOOK = {
  next: { box: "border-indigo-400/40 bg-indigo-500/10", text: "text-indigo-200", lead: "Next", Icon: ArrowRight },
  waiting: { box: "border-slate-500/40 bg-slate-500/10", text: "text-slate-200", lead: "Waiting", Icon: Clock },
  stopped: { box: "border-red-500/40 bg-red-500/10", text: "text-red-200", lead: "Stopped", Icon: OctagonAlert },
  done: { box: "border-teal/40 bg-teal/10", text: "text-teal", lead: "Done", Icon: CheckCircle2 },
};

// BM-H3368: the FORF page is light, the rest of the app dark.
const LIGHT_TEXT = {
  next: "text-indigo-800", waiting: "text-slate-700", stopped: "text-red-800", done: "text-teal-800",
};

export default function NextStepBanner({ matterRef, status, compact = false, light = false, refreshKey }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [fetched, setFetched] = useState(null);

  useEffect(() => {
    if (status !== undefined || !matterRef) return undefined;
    let cancelled = false;
    axios.get(`${API}/pipeline/matter/${encodeURIComponent(matterRef)}/status`)
      .then((r) => { if (!cancelled) setFetched(r.data || null); })
      .catch(() => { if (!cancelled) setFetched(null); });
    return () => { cancelled = true; };
  }, [matterRef, status, refreshKey]);

  const ns = (status !== undefined ? status : fetched)?.next_step;
  if (!ns || !LOOK[ns.state]) return null;
  const look = light ? { ...LOOK[ns.state], text: LIGHT_TEXT[ns.state] } : LOOK[ns.state];
  // BM-H3368: on the page the button would open, the button is noise.
  const here = location.pathname;
  const onItsPage = ns.action && (
    (ns.action.kind === "path" && ns.action.path === here)
    || (ns.action.kind === "conflict_check" && ns.action.conflict_check_id
        && here === `/conflict-check/${encodeURIComponent(ns.action.conflict_check_id)}`));
  const action = onItsPage ? null : ns.action;

  const run = (e) => {
    // Inside a tracker row a click would also toggle the row open.
    if (e && e.stopPropagation) e.stopPropagation();
    if (!action) return;
    const onError = (msg) => toast.error(msg);
    if (action.kind === "conflict_check") {
      openConflictCheck({
        conflictCheckId: action.conflict_check_id, matterRef, axios, apiBase: API, navigate, onError,
      });
    } else if (action.kind === "ccl") {
      openMatterCcl({ matterRef, axios, apiBase: API, navigate, onError });
    } else if (action.path) {
      navigate(action.path);
    }
  };

  if (compact) {
    // BM-H3368: one line for a pipeline tracker row.
    return (
      <div className="flex items-center gap-2 mt-1 min-w-0" data-testid="next-step-line" data-state={ns.state}>
        <look.Icon className={`w-3 h-3 flex-shrink-0 ${look.text}`} />
        <span className={`text-[11px] font-medium truncate ${look.text}`} title={ns.detail || ns.title}>
          {look.lead}: {ns.title}
        </span>
        {action && (
          <button
            type="button"
            onClick={run}
            className="text-[11px] underline text-slate-200 hover:text-white flex-shrink-0"
            data-testid="next-step-line-action"
          >
            {action.label}
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={`rounded-md border ${look.box} px-4 py-3 mb-4 flex items-start gap-3`}
      data-testid="next-step-banner"
      data-state={ns.state}
    >
      <look.Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${look.text}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${look.text}`} data-testid="next-step-title">
          {look.lead}: {ns.title}
        </p>
        {ns.detail && (
          <p className={`text-xs mt-0.5 ${light ? "text-slate-700" : "text-slate-300"}`} data-testid="next-step-detail">{ns.detail}</p>
        )}
      </div>
      {action && (
        <Button size="sm" onClick={run} data-testid="next-step-action" className="flex-shrink-0">
          {action.label}
        </Button>
      )}
    </div>
  );
}
